import { useLocale } from '@/components/Link'
import getLocalizedHref from '@/components/Link/getLocalizedHref'
import { useSupport } from '@/components/Providers/SupportProvider'
import useTenant from '@/hooks/useTenant'
import { PersonRounded } from '@mui/icons-material'
import { Alert, Button, GridLegacy as Grid, Stack, TextField, Typography } from '@mui/material'

import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PageType, SignUpData } from '.'
import HeaderRoundedIcon from '../headerRoundedIcon'
import { validateRules } from './rules'

type Props = {
  onSubmit?: (values: SignupFormValues) => void | Promise<void>
  title: string
  description: string
  buttonText: string
  error?: string
  pageType: PageType
  companyName?: string
  companyCode?: string
}

type ErrorsState = {
  firstName?: string[]
  lastName?: string[]
  email?: string[]
  phone?: string[]
  companyName?: string[]
  companyCode?: string[]
}

export type SignupFormValues = Pick<
  SignUpData,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'companyName'
  | 'companyCode'
  | 'subscribeNewsletter'
>

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  companyCode: '',
}

const SignUpForm = (props: Props) => {
  const { t } = useTranslation()

  const {
    title,
    description,
    buttonText,
    onSubmit = () => null,
    error,
    pageType,
    companyName: initialCompanyName,
    companyCode: initialCompanyCode,
  } = props

  const locale = useLocale()
  const [errors, setErrors] = useState<ErrorsState>({})
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState(INITIAL_STATE)
  const support = useSupport()
  const tenant = useTenant()

  useEffect(() => {
    setFormData({
      ...formData,
      companyName: initialCompanyName || formData.companyName,
      companyCode: initialCompanyCode || formData.companyCode,
    })
  }, [initialCompanyName, initialCompanyCode])

  useEffect(() => {
    const result = validateRules(formData, pageType)
    if (result.success) setErrors({})
    else setErrors(result.errors)
  }, [formData])

  const { firstName, lastName, email, phone, companyName, companyCode } = formData

  const isValid = () => {
    const result = validateRules(formData, pageType)
    return result.success
  }

  const handleChange =
    (field: string) =>
    ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) =>
      setFormData((prev) => ({ ...prev, [field]: value }))

  const submit = (e: React.MouseEvent | React.FormEvent) => {
    setSubmitted(true)
    e.preventDefault()
    if (isValid()) {
      void onSubmit(formData)
    }
  }

  const showErrors = !isValid() && submitted

  const customErrors = []
  if (error) customErrors.push(error)

  return (
    <Stack spacing={2}>
      <HeaderRoundedIcon>
        <PersonRounded sx={{ fontSize: '60px' }} color="primary" />
      </HeaderRoundedIcon>
      <Typography textAlign="center" variant="h3" fontWeight="400">
        {title}
      </Typography>
      <Typography textAlign="center" variant="body1">
        {description}
      </Typography>

      <Grid container spacing={1} paddingRight={1}>
        <Grid item xs={6} display="inline-flex" alignItems="center">
          <TextField
            fullWidth
            value={firstName}
            label={t('authentication:formLabels:firstName')}
            id="firstName"
            error={showErrors && Boolean(errors.firstName)}
            helperText={showErrors ? errors.firstName?.join(', ') : ''}
            type="firstName"
            variant="outlined"
            onChange={handleChange('firstName')}
            data-testid="signup-first-name-input"
            autoComplete="given-name"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        <Grid item xs={6} display="inline-flex" alignItems="center">
          <TextField
            fullWidth
            value={lastName}
            label={t('authentication:formLabels:lastName')}
            id="lastName"
            error={showErrors && Boolean(errors.lastName)}
            helperText={showErrors ? errors.lastName?.join(', ') : ''}
            type="lastName"
            variant="outlined"
            onChange={handleChange('lastName')}
            data-testid="signup-last-name-input"
            autoComplete="family-name"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        {pageType === 'gm' && (
          <Grid item xs={12} display="inline-flex" alignItems="center">
            <TextField
              fullWidth
              value={companyName}
              label={t('authentication:formLabels:companyName')}
              id="companyName"
              error={showErrors && Boolean(errors.companyName)}
              helperText={showErrors ? errors.companyName?.join(', ') : ''}
              type="companyName"
              variant="outlined"
              onChange={handleChange('companyName')}
              autoComplete="organization"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        )}

        <Grid item xs={pageType === 'gm' ? 12 : 6} display="inline-flex" alignItems="center">
          <TextField
            fullWidth
            value={email}
            label={t('authentication:formLabels:email')}
            id="email"
            error={showErrors && Boolean(errors.email)}
            helperText={showErrors ? errors.email?.join(', ') : ''}
            type="email"
            variant="outlined"
            onChange={handleChange('email')}
            data-testid="signup-email-input"
            autoComplete="email"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        <Grid item xs={pageType === 'gm' ? 12 : 6} display="inline-flex" alignItems="center">
          <TextField
            fullWidth
            value={phone}
            label={t('authentication:formLabels:phone')}
            id="phone"
            error={showErrors && Boolean(errors.phone)}
            helperText={showErrors ? errors.phone?.join(', ') : ''}
            type="phone"
            variant="outlined"
            onChange={handleChange('phone')}
            data-testid="signup-phone-number-input"
            autoComplete="tel"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>

        {pageType === 'cc' && !initialCompanyCode && (
          <>
            <Grid item xs={12} display="inline-flex" alignItems="center">
              <TextField
                fullWidth
                value={companyCode}
                label={t('authentication:formLabels:companyCode')}
                id="companyCode"
                error={showErrors && Boolean(errors.companyCode)}
                helperText={showErrors ? errors.companyCode?.join(', ') : ''}
                type="companyCode"
                variant="outlined"
                onChange={handleChange('companyCode')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>

            <Typography variant="body2" textAlign="center" mt={2} lineHeight={1}>
              Enter the code you have seen at work or received from your Office Manager. No code?
              <Button onClick={() => support.show()} variant="text">
                Get in touch
              </Button>
            </Typography>
          </>
        )}
      </Grid>

      {customErrors.length > 0 && (
        <Alert icon={false} severity="error">
          {customErrors.map((error) => (
            <React.Fragment key={error.length}>
              {error}
              <br />
            </React.Fragment>
          ))}
        </Alert>
      )}

      <Button
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        onClick={submit}
        data-testid="signup-complete-button">
        {buttonText}
      </Button>

      {pageType === 'cc' && initialCompanyCode && (
        <div style={{ marginTop: '20px' }}>
          <Typography variant="body2" textAlign="center">
            Already signed up?
            <Button
              sx={{ minWidth: 'auto' }}
              variant="text"
              href={getLocalizedHref('/authentication/login', locale)}>
              Log in
            </Button>
          </Typography>

          <Typography variant="body2" textAlign="center">
            Not sure if your company has {tenant.name}?
            <Button onClick={() => support.show()} variant="text">
              Get in touch
            </Button>
          </Typography>
        </div>
      )}
    </Stack>
  )
}

export default SignUpForm
